<template>
  <div class="task-relations">
    <!-- Outgoing Relations (this task -> other tasks) -->
    <div v-if="outgoingRelations.length > 0" class="relation-group">
      <div
        v-for="relation in outgoingRelations"
        :key="relation.id"
        class="relation-item"
        :style="{ borderLeftColor: relation.horizontalRelationType.color || '#6b7280' }"
      >
        <template v-if="editingRelationId === relation.id">
          <!-- Edit mode -->
          <div class="edit-relation-form">
            <div class="form-row">
              <label style="display: block; margin-bottom: 4px; font-size: 12px; color: #666">
                {{ s__('TaskDetail|Relation Type') }}
              </label>
              <select v-model="editingRelation.typeId" class="form-select">
                <option value="">{{ s__('TaskDetail|Select relation type...') }}</option>
                <option v-for="type in relationTypes" :key="type.id" :value="type.id">
                  {{ type.directTitle }} / {{ type.reverseTitle }}
                </option>
              </select>
            </div>
            <div class="form-row">
              <label style="display: block; margin-bottom: 4px; font-size: 12px; color: #666">
                {{ s__('TaskDetail|Direction') }}
              </label>
              <select v-model="editingRelation.direction" class="form-select">
                <option value="outgoing">
                  {{ s__('TaskDetail|This task') }} → {{ s__('TaskDetail|Other task') }}
                </option>
                <option value="incoming">
                  {{ s__('TaskDetail|Other task') }} → {{ s__('TaskDetail|This task') }}
                </option>
              </select>
            </div>
            <div class="form-row">
              <label style="display: block; margin-bottom: 4px; font-size: 12px; color: #666">
                {{ s__('TaskDetail|Target Task') }}
              </label>
              <input
                type="text"
                :value="`#${relation.targetTask.iid} ${relation.targetTask.title}`"
                disabled
                class="form-select"
                style="opacity: 0.6; cursor: not-allowed"
              />
            </div>
            <div class="form-actions">
              <button
                :disabled="!canSaveRelation || updating"
                class="btn-sm"
                @click="saveRelation(relation.id)"
              >
                {{ updating ? s__('TaskDetail|Saving...') : s__('TaskDetail|Save') }}
              </button>
              <button class="btn-sm btn-secondary" @click="cancelEdit">{{ __('Cancel') }}</button>
            </div>
            <div v-if="updateError" class="error">{{ updateError }}</div>
          </div>
        </template>
        <template v-else>
          <!-- View mode -->
          <span v-if="relation.horizontalRelationType.icon" class="relation-icon">{{
            relation.horizontalRelationType.icon
          }}</span>
          <span class="relation-type-label">{{ relation.horizontalRelationType.directTitle }}</span>
          <a :href="taskUrl(relation.targetTask)" class="related-task-link">
            #{{ relation.targetTask.iid }} {{ relation.targetTask.title }}
          </a>
          <button
            v-if="canManageRelations"
            class="btn-edit"
            :title="s__('TaskDetail|Edit relation')"
            @click="startEdit(relation)"
          >
            <EditIcon />
          </button>
          <button
            v-if="canManageRelations"
            class="btn-remove"
            :title="s__('TaskDetail|Remove relation')"
            @click="removeRelation(relation.id)"
          >
            ×
          </button>
        </template>
      </div>
    </div>

    <!-- Incoming Relations (other tasks -> this task) -->
    <div v-if="incomingRelations.length > 0" class="relation-group">
      <div
        v-for="relation in incomingRelations"
        :key="relation.id"
        class="relation-item incoming"
        :style="{ borderLeftColor: relation.horizontalRelationType.color || '#6b7280' }"
      >
        <template v-if="editingRelationId === relation.id">
          <!-- Edit mode -->
          <div class="edit-relation-form">
            <div class="form-row">
              <label style="display: block; margin-bottom: 4px; font-size: 12px; color: #666">
                {{ s__('TaskDetail|Relation Type') }}
              </label>
              <select v-model="editingRelation.typeId" class="form-select">
                <option value="">{{ s__('TaskDetail|Select relation type...') }}</option>
                <option v-for="type in relationTypes" :key="type.id" :value="type.id">
                  {{ type.directTitle }} / {{ type.reverseTitle }}
                </option>
              </select>
            </div>
            <div class="form-row">
              <label style="display: block; margin-bottom: 4px; font-size: 12px; color: #666">
                {{ s__('TaskDetail|Direction') }}
              </label>
              <select v-model="editingRelation.direction" class="form-select">
                <option value="outgoing">
                  {{ s__('TaskDetail|This task') }} → {{ s__('TaskDetail|Other task') }}
                </option>
                <option value="incoming">
                  {{ s__('TaskDetail|Other task') }} → {{ s__('TaskDetail|This task') }}
                </option>
              </select>
            </div>
            <div class="form-row">
              <label style="display: block; margin-bottom: 4px; font-size: 12px; color: #666">
                {{ s__('TaskDetail|Source Task') }}
              </label>
              <input
                type="text"
                :value="`#${relation.sourceTask.iid} ${relation.sourceTask.title}`"
                disabled
                class="form-select"
                style="opacity: 0.6; cursor: not-allowed"
              />
            </div>
            <div class="form-actions">
              <button
                :disabled="!canSaveRelation || updating"
                class="btn-sm"
                @click="saveRelation(relation.id)"
              >
                {{ updating ? s__('TaskDetail|Saving...') : s__('TaskDetail|Save') }}
              </button>
              <button class="btn-sm btn-secondary" @click="cancelEdit">{{ __('Cancel') }}</button>
            </div>
            <div v-if="updateError" class="error">{{ updateError }}</div>
          </div>
        </template>
        <template v-else>
          <!-- View mode -->
          <span v-if="relation.horizontalRelationType.icon" class="relation-icon">{{
            relation.horizontalRelationType.icon
          }}</span>
          <span class="relation-type-label">{{
            relation.horizontalRelationType.reverseTitle
          }}</span>
          <a :href="taskUrl(relation.sourceTask)" class="related-task-link">
            #{{ relation.sourceTask.iid }} {{ relation.sourceTask.title }}
          </a>
          <button
            v-if="canManageRelations"
            class="btn-edit"
            :title="s__('TaskDetail|Edit relation')"
            @click="startEdit(relation)"
          >
            <EditIcon />
          </button>
          <button
            v-if="canManageRelations"
            class="btn-remove"
            :title="s__('TaskDetail|Remove relation')"
            @click="removeRelation(relation.id)"
          >
            ×
          </button>
        </template>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="outgoingRelations.length === 0 && incomingRelations.length === 0 && !showAddForm"
      class="muted"
    >
      {{ s__('TaskDetail|No relations') }}
    </div>

    <!-- Add relation button -->
    <button
      v-if="canManageRelations && relationTypes.length > 0 && !showAddForm"
      class="btn-add-relation mobile-stretch-btn"
      @click="showAddForm = true"
    >
      {{ s__('TaskDetail|Add relation') }}
    </button>

    <!-- Add relation form -->
    <div v-if="showAddForm" class="add-relation-form">
      <div class="form-row">
        <select v-model="newRelation.typeId" class="form-select">
          <option value="">{{ s__('TaskDetail|Select relation type...') }}</option>
          <option v-for="type in relationTypes" :key="type.id" :value="type.id">
            {{ type.directTitle }} / {{ type.reverseTitle }}
          </option>
        </select>
      </div>
      <div class="form-row">
        <select v-model="newRelation.direction" class="form-select">
          <option value="outgoing">
            {{ s__('TaskDetail|This task') }} → {{ s__('TaskDetail|Other task') }}
          </option>
          <option value="incoming">
            {{ s__('TaskDetail|Other task') }} → {{ s__('TaskDetail|This task') }}
          </option>
        </select>
      </div>
      <div class="form-row">
        <TaskSelect
          v-model="newRelation.targetTaskId"
          :project-id="projectId"
          :placeholder="s__('TaskDetail|Select task...')"
          :search-placeholder="s__('TaskDetail|Search tasks...')"
          :exclude-task-ids="[taskId]"
        />
      </div>
      <div class="form-actions">
        <button :disabled="!canAddRelation || adding" class="btn-sm" @click="addRelation">
          {{ adding ? s__('TaskDetail|Adding...') : s__('TaskDetail|Add') }}
        </button>
        <button class="btn-sm btn-secondary" @click="cancelAdd">{{ __('Cancel') }}</button>
      </div>
      <div v-if="addError" class="error">{{ addError }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { s__, __ } from '@/locale'
import TaskSelect from '@/components/common/TaskSelect.vue'
import EditIcon from '../../common/icons/EditIcon.vue'

const props = defineProps({
  taskId: { type: String, required: true },
  projectId: { type: String, required: true },
  outgoingRelations: { type: Array, default: () => [] },
  incomingRelations: { type: Array, default: () => [] },
  relationTypes: { type: Array, default: () => [] },
  canManageRelations: { type: Boolean, default: false },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
})

const emit = defineEmits(['relation-added', 'relation-removed', 'relation-updated'])

const showAddForm = ref(false)
const adding = ref(false)
const addError = ref('')
const newRelation = ref({
  typeId: '',
  direction: 'outgoing',
  targetTaskId: '',
})

const editingRelationId = ref(null)
const editingRelation = ref({
  typeId: '',
  direction: 'outgoing',
})
const updating = ref(false)
const updateError = ref('')

const canAddRelation = computed(() => {
  return newRelation.value.typeId && newRelation.value.targetTaskId
})

const canSaveRelation = computed(() => {
  return editingRelation.value.typeId && editingRelation.value.typeId !== ''
})

const CREATE_RELATION = gql`
  mutation CreateTaskRelation(
    $horizontalRelationTypeId: ID!
    $sourceTaskId: ID!
    $targetTaskId: ID!
  ) {
    createTaskRelation(
      horizontalRelationTypeId: $horizontalRelationTypeId
      sourceTaskId: $sourceTaskId
      targetTaskId: $targetTaskId
    ) {
      taskRelation {
        id
      }
      errors
    }
  }
`

const DESTROY_RELATION = gql`
  mutation DestroyTaskRelation($id: ID!) {
    destroyTaskRelation(id: $id) {
      taskRelation {
        id
      }
      errors
    }
  }
`

const UPDATE_RELATION = gql`
  mutation UpdateTaskRelation(
    $id: ID!
    $horizontalRelationTypeId: ID!
    $direction: String
    $currentTaskId: ID
  ) {
    updateTaskRelation(
      id: $id
      horizontalRelationTypeId: $horizontalRelationTypeId
      direction: $direction
      currentTaskId: $currentTaskId
    ) {
      taskRelation {
        id
      }
      errors
    }
  }
`

const { mutate: createRelationMutation } = useMutation(CREATE_RELATION)
const { mutate: destroyRelationMutation } = useMutation(DESTROY_RELATION)
const { mutate: updateRelationMutation } = useMutation(UPDATE_RELATION)

function taskUrl(task) {
  const taskSegment = task.iid
  if (!taskSegment) return '#'
  let url = `/projects/${props.projectId}/tasks/${taskSegment}`
  const params = []
  if (props.chatId) params.push(`chat_id=${props.chatId}`)
  if (props.fromId) params.push(`from_id=${props.fromId}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
}

function cancelAdd() {
  showAddForm.value = false
  newRelation.value = { typeId: '', direction: 'outgoing', targetTaskId: '' }
  addError.value = ''
}

async function addRelation() {
  if (!canAddRelation.value) return

  adding.value = true
  addError.value = ''

  try {
    // Determine source and target based on direction
    const sourceTaskId =
      newRelation.value.direction === 'outgoing' ? props.taskId : newRelation.value.targetTaskId
    const targetTaskId =
      newRelation.value.direction === 'outgoing' ? newRelation.value.targetTaskId : props.taskId

    const { data } = await createRelationMutation({
      horizontalRelationTypeId: newRelation.value.typeId,
      sourceTaskId,
      targetTaskId,
    })

    if (data?.createTaskRelation?.errors?.length) {
      const errorMessages = data.createTaskRelation.errors
        .map(e => (typeof e === 'string' ? e : e.message || String(e)))
        .join(', ')
      addError.value = errorMessages
    } else {
      cancelAdd()
      emit('relation-added')
    }
  } catch (e) {
    addError.value =
      s__('TaskDetail|Failed to add relation') +
      ': ' +
      (e.message || s__('TaskDetail|Unknown error'))
  } finally {
    adding.value = false
  }
}

function startEdit(relation) {
  editingRelationId.value = relation.id
  // Determine current direction: if this task is source, it's outgoing; if target, it's incoming
  // Check both sourceTask and targetTask to handle both outgoing and incoming relations
  // Convert IDs to strings for comparison to handle string/number mismatches
  const currentTaskId = String(props.taskId)
  const isOutgoing = relation.sourceTask && String(relation.sourceTask.id) === currentTaskId
  const isIncoming = relation.targetTask && String(relation.targetTask.id) === currentTaskId
  // Default to outgoing if we can't determine (shouldn't happen, but safety check)
  const direction = isOutgoing ? 'outgoing' : isIncoming ? 'incoming' : 'outgoing'
  editingRelation.value = {
    typeId: relation.horizontalRelationType.id,
    direction: direction,
  }
  updateError.value = ''
}

function cancelEdit() {
  editingRelationId.value = null
  editingRelation.value = { typeId: '', direction: 'outgoing' }
  updateError.value = ''
}

async function saveRelation(relationId) {
  if (!canSaveRelation.value) return

  updating.value = true
  updateError.value = ''

  try {
    const { data } = await updateRelationMutation({
      id: relationId,
      horizontalRelationTypeId: editingRelation.value.typeId,
      direction: editingRelation.value.direction,
      currentTaskId: props.taskId,
    })

    if (data?.updateTaskRelation?.errors?.length) {
      const errorMessages = data.updateTaskRelation.errors
        .map(e => (typeof e === 'string' ? e : e.message || String(e)))
        .join(', ')
      updateError.value = errorMessages
    } else {
      cancelEdit()
      emit('relation-updated')
    }
  } catch (e) {
    updateError.value =
      s__('TaskDetail|Failed to update relation') +
      ': ' +
      (e.message || s__('TaskDetail|Unknown error'))
  } finally {
    updating.value = false
  }
}

async function removeRelation(relationId) {
  if (!confirm(s__('TaskDetail|Are you sure you want to remove this relation?'))) return

  try {
    const { data } = await destroyRelationMutation({ id: relationId })

    if (data?.destroyTaskRelation?.errors?.length) {
      const errorMessages = data.destroyTaskRelation.errors
        .map(e => (typeof e === 'string' ? e : e.message || String(e)))
        .join(', ')
      alert(errorMessages)
    } else {
      emit('relation-removed')
    }
  } catch (e) {
    alert(
      s__('TaskDetail|Failed to remove relation') +
        ': ' +
        (e.message || s__('TaskDetail|Unknown error'))
    )
  }
}
</script>

<style scoped>
.task-relations {
  margin-top: 8px;
}

.relation-group {
  margin-bottom: 8px;
}

.relation-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f5f5f5;
  border-radius: 4px;
  border-left: 3px solid;
  margin-bottom: 4px;
  font-size: 14px;
}

.relation-icon {
  font-size: 16px;
  line-height: 1;
}

.relation-type-label {
  font-weight: 500;
  font-size: 12px;
  color: #666;
  min-width: 80px;
}

.related-task-link {
  flex: 1;
  color: #2aabee;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.related-task-link:hover {
  text-decoration: underline;
}

.btn-edit {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 4px;
}

.btn-edit:hover {
  color: #2aabee;
}

.btn-remove {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: 0 4px;
}

.btn-remove:hover {
  color: #dc2626;
}

.edit-relation-form {
  width: 100%;
  padding: 8px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.btn-add-relation {
  background: none;
  border: 1px dashed #d1d5db;
  color: #6b7280;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  margin-top: 8px;
  transition: all 0.15s ease-in-out;
}

.btn-add-relation:hover {
  border-color: #2aabee;
  color: #2aabee;
  background: rgba(42, 171, 238, 0.05);
}

.add-relation-form {
  margin-top: 12px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 4px;
}

.form-row {
  margin-bottom: 8px;
}

.form-select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 14px;
}

.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

/* Button styles inherit from forms.css */

.muted {
  color: #888;
  font-size: 14px;
}

.error {
  color: #dc2626;
  font-size: 13px;
  margin-top: 8px;
}
</style>
